/**
 * @module @repo/global-login-sdk/login-dialog
 * Einbettbarer Magic-Link-Login-Dialog (inline, ohne Redirect). Verschickt per
 * `signInWithOtp` einen Anmeldelink an die E-Mail und leitet nach Klick auf
 * `<app-origin>/auth/callback`. Optional bietet er Google-OAuth über ein Popup
 * (`useLoginPopup`), da OAuth im eingebetteten Kontext (iframe) nicht inline
 * funktioniert und deshalb ein separates Fenster braucht.
 */
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useGlobalLogin, useLoginPopup } from './react';
import { Dialog, DialogButton, Spinner } from './dialog';
import { DialogResult } from './dialog-result';
import { cn } from './cn';

/** Absichtlich permissive E-Mail-Validierung — die echte Prüfung passiert serverseitig. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Statuswerte des Magic-Link-Versands. */
type LoginStatus = 'idle' | 'pending' | 'success' | 'error';

/** Props von {@link LoginDialog}. */
export interface LoginDialogProps {
  /** Sichtbarkeit des Dialogs. */
  open: boolean;
  /** Schließen-Callback. */
  onClose: () => void;
  /** Optionaler Klartext-Name der App (für den Titel). */
  appLabel?: string;
  /** Zusätzlichen „Mit Google anmelden"-Button (via Popup) anbieten. Default `false`. */
  googleOAuth?: boolean;
}

/** Google-„G"-Wortmarke in offiziellen Markenfarben (rein dekorativ). */
function GoogleGlyph(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4">
      <path
        fill="#4285F4"
        d="M23.06 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h6.2a5.3 5.3 0 0 1-2.3 3.48v2.9h3.72c2.18-2 3.44-4.96 3.44-8.39Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.1 0 5.7-1.03 7.6-2.79l-3.72-2.89c-1.03.69-2.35 1.1-3.88 1.1-2.98 0-5.5-2.01-6.4-4.72H1.76v2.98A11.5 11.5 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.6 14.7a6.9 6.9 0 0 1 0-4.4V7.32H1.76a11.5 11.5 0 0 0 0 10.36l3.84-2.98Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.68 0 3.19.58 4.38 1.72l3.28-3.28C17.7 1.2 15.1 0 12 0A11.5 11.5 0 0 0 1.76 6.32L5.6 9.3C6.5 6.6 9.02 4.75 12 4.75Z"
      />
    </svg>
  );
}

/**
 * Einbettbarer Login-Dialog mit Magic-Link (inline). Deckt alle UI-Zustände ab:
 * Formular (idle), Versand (pending), Bestätigung (success) und Fehler (error);
 * E-Mail wird per Regex validiert (`aria-invalid` + `role="alert"`-Fehlermeldung,
 * Fokus springt bei ungültigem Submit auf das Feld). Optional öffnet ein
 * „Mit Google anmelden"-Button den zentralen Login als Popup (siehe
 * {@link useLoginPopup}) und schließt den Dialog bei Erfolg; scheitert der
 * Popup-Flow, wird eine eigene Fehlermeldung angesagt. Der Success-Zustand ist
 * eine `role="status"`-Live-Region und holt den Fokus in den Dialog zurück,
 * sodass Screenreader ihn ansagen und Esc weiter greift.
 *
 * @param props Siehe {@link LoginDialogProps}.
 * @returns Der gerenderte Dialog.
 */
export function LoginDialog(props: LoginDialogProps): ReactNode {
  const { open, onClose, appLabel, googleOAuth = false } = props;
  const { client } = useGlobalLogin();
  const popup = useLoginPopup();
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState<LoginStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const emailId = useId();
  const errorId = useId();
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setTouched(false);
      setStatus('idle');
      setError(null);
      setGoogleError(null);
    }
  }, [open]);

  const emailValid = EMAIL_PATTERN.test(email.trim());
  const pending = status === 'pending';
  const showEmailError = touched && !emailValid;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setTouched(true);
    if (!emailValid) {
      // Fokus auf das ungültige Feld, damit die Fehlermeldung (role="alert" +
      // aria-describedby) angesagt wird und nicht am Button „hängen bleibt".
      emailInputRef.current?.focus();
      return;
    }
    setStatus('pending');
    setError(null);
    try {
      const emailRedirectTo =
        typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined;
      const { error: otpError } = await client.auth.signInWithOtp({
        email: email.trim(),
        options: emailRedirectTo ? { emailRedirectTo } : undefined,
      });
      if (otpError) {
        setError(otpError.message || 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.');
        setStatus('error');
        return;
      }
      setStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen.');
      setStatus('error');
    }
  }

  async function handleGoogle(): Promise<void> {
    setGoogleError(null);
    const signedIn = await popup.open();
    if (signedIn) {
      onClose();
      return;
    }
    // M1: Popup-Flow fehlgeschlagen/abgebrochen (open() → false bzw. Popup blockiert)
    // sichtbar melden — eigener, beim Öffnen zurückgesetzter Fehlerkanal.
    setGoogleError(
      'Die Google-Anmeldung ist fehlgeschlagen oder wurde abgebrochen. Erlaube ggf. Popups und versuche es erneut.',
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={appLabel ? `Bei ${appLabel} anmelden` : 'Anmelden'}
      description="Melde dich mit deiner E-Mail an — wir schicken dir einen Anmeldelink."
    >
      {status === 'success' ? (
        <DialogResult
          icon={
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              className="mt-0.5 size-5 shrink-0 text-emerald-600"
            >
              <rect
                x="3"
                y="5"
                width="18"
                height="14"
                rx="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M4 7l8 6 8-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          onClose={onClose}
        >
          Prüfe deine E-Mail — wir haben dir einen Anmeldelink geschickt.
        </DialogResult>
      ) : (
        <form
          onSubmit={(event) => void handleSubmit(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          {status === 'error' && error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <label htmlFor={emailId} className="text-sm font-medium">
              E-Mail
            </label>
            <input
              ref={emailInputRef}
              id={emailId}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setTouched(true)}
              disabled={pending}
              aria-invalid={showEmailError || undefined}
              aria-describedby={showEmailError ? errorId : undefined}
              placeholder="du@beispiel.de"
              className={cn(
                'w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground',
                'focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:outline-none disabled:opacity-60',
                showEmailError && 'border-destructive/60 focus-visible:ring-destructive/40',
              )}
            />
            {showEmailError ? (
              <p id={errorId} role="alert" className="text-sm text-destructive">
                Bitte gib eine gültige E-Mail-Adresse ein.
              </p>
            ) : null}
          </div>
          <DialogButton type="submit" disabled={pending} className="w-full">
            {pending ? (
              <>
                <Spinner />
                Sende Link …
              </>
            ) : (
              'Anmeldelink senden'
            )}
          </DialogButton>

          {googleOAuth ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-foreground/15" />
                oder
                <span className="h-px flex-1 bg-foreground/15" />
              </div>
              <DialogButton
                variant="secondary"
                onClick={() => void handleGoogle()}
                disabled={popup.status === 'pending'}
                className="w-full"
              >
                {popup.status === 'pending' ? <Spinner /> : <GoogleGlyph />}
                Mit Google anmelden
              </DialogButton>
              {googleError ? (
                <p
                  role="alert"
                  className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {googleError}
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                Es öffnet sich ein Fenster zum zentralen Login.
              </p>
            </div>
          ) : null}
        </form>
      )}
    </Dialog>
  );
}
